import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useMemo, useRef, useState } from "react";
import { appConfirmDialogClasses, appInnerCardStyle, appMutedTextStyle, appNeutralPanelStyle, renderConfirmMessage } from "../../../components/general/uiSurfaceStyles";
import {
  combineLosAngelesDateAndTime,
  formatLosAngelesDateTimeLocalInput,
  getLosAngelesCalendarValue,
  getLosAngelesDateKey,
  getLosAngelesDateKeyFromCalendarValue,
  getLosAngelesTimeKey,
  LOS_ANGELES_TIME_OPTIONS,
  updateLosAngelesDateTimePart,
} from "../../../utils/losAngelesDateTime";

type BehaviorOption = {
  behaviorKey: string;
  behaviorTitle: string;
  sectionCategory?: string;
  presentBehaviorSuggestions?: string[];
  instructionSuggestions?: string[];
  carePlanInstructions?: string;
  carePlanDetails?: string;
  caregiverInstructions?: string;
};

type DailyBehaviorLogPanelProps = {
  behaviorOptions: BehaviorOption[];
  entries: any[];
  loading?: boolean;
  saving?: boolean;
  emptyMessage?: string;
  canGenerateReport?: boolean;
  canEditEntry?: (entry: any) => boolean;
  collapsibleComposer?: boolean;
  defaultComposerVisible?: boolean;
  showComposerToggleButton?: boolean;
  composerVisible?: boolean;
  onComposerVisibleChange?: (visible: boolean) => void;
  openComposerSignal?: number;
  closeComposerSignal?: number;
  showComposerSection?: boolean;
  collapsibleEntries?: boolean;
  defaultEntriesVisible?: boolean;
  entriesVisible?: boolean;
  onEntriesVisibleChange?: (visible: boolean) => void;
  hideEntriesSection?: boolean;
  showEntriesSection?: boolean;
  showEntriesToggleButton?: boolean;
  showDraftsSection?: boolean;
  serverEntriesPage?: number;
  serverEntriesPageSize?: number;
  serverEntriesHasPreviousPage?: boolean;
  serverEntriesHasNextPage?: boolean;
  onEntriesPreviousPage?: () => void;
  onEntriesNextPage?: () => void;
  secondaryActionLabel?: string;
  onCreateEntry: (payload: Record<string, any>) => Promise<void>;
  onUpdateEntry: (behaviorLogEntryId: string, payload: Record<string, any>) => Promise<void>;
  onDeleteDraft?: (behaviorLogEntryId: string) => Promise<void>;
  onFormatText?: (request: {
    text: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
    metadata?: Record<string, any>;
  }) => Promise<{ formattedText?: string } | null | undefined>;
  onGenerateCaregiverInterventions?: (request: {
    behaviorTitle: string;
    behaviorCategory?: string;
    behaviorDetails?: string;
  }) => Promise<{ formattedText?: string } | null | undefined>;
  onGenerateReport?: () => void;
  onSecondaryAction?: () => void;
  onDraftSaved?: () => void;
  onOpenCarePlan?: () => void;
  openCarePlanLabel?: string;
  prnMedicationOptions?: Array<{ label: string; value: string; medicationName?: string }>;
  loadingPrnMedicationOptions?: boolean;
  allowRecentEntryReuse?: boolean;
};

type FormState = {
  behaviorKey: string;
  behaviorKeys: string[];
  customBehaviorTitle: string;
  residentPresentBehavior: string;
  instructionsGiven: string;
  residentResponse: string;
  notes: string;
  startedAt: string;
  endedAt: string;
  isOngoing: boolean;
  prnUsed: boolean;
  prnEntryMode: "manual" | "emar";
  linkedPrnMedicationId: string;
  linkedPrnMedicationName: string;
  prnMedicationName: string;
  prnDetails: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;
const CUSTOM_BEHAVIOR_KEY = "__custom_behavior__";
const TODAY_DATE_KEY = getLosAngelesDateKey();

const formatDateTimeLocalInput = (value?: string) => {
  return formatLosAngelesDateTimeLocalInput(value);
};

const getDefaultFormState = (defaultBehaviorKey = ""): FormState => ({
  behaviorKey: defaultBehaviorKey,
  behaviorKeys: defaultBehaviorKey ? [defaultBehaviorKey] : [],
  customBehaviorTitle: "",
  residentPresentBehavior: "",
  instructionsGiven: "",
  residentResponse: "",
  notes: "",
  startedAt: formatDateTimeLocalInput(),
  endedAt: "",
  isOngoing: true,
  prnUsed: false,
  prnEntryMode: "manual",
  linkedPrnMedicationId: "",
  linkedPrnMedicationName: "",
  prnMedicationName: "",
  prnDetails: "",
});

const addMinutesToLocalInput = (value: string, minutes: number) => {
  const baseIso = value ? toIsoString(value) : "";
  const baseDate = baseIso ? new Date(baseIso) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return formatDateTimeLocalInput();
  }
  return formatDateTimeLocalInput(new Date(baseDate.getTime() + minutes * 60000).toISOString());
};

const toIsoString = (value: string) => {
  if (!value) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    return combineLosAngelesDateAndTime(getLosAngelesDateKey(value), getLosAngelesTimeKey(value));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return "Not set";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" });
};

const formatBehaviorDuration = (startedAt: string, endedAt: string, isOngoing: boolean) => {
  if (isOngoing || !startedAt || !endedAt) {
    return "";
  }

  const startedAtDate = new Date(toIsoString(startedAt));
  const endedAtDate = new Date(toIsoString(endedAt));
  if (Number.isNaN(startedAtDate.getTime()) || Number.isNaN(endedAtDate.getTime())) {
    return "";
  }

  const diffMinutes = Math.round((endedAtDate.getTime() - startedAtDate.getTime()) / 60000);
  if (diffMinutes < 0) {
    return "";
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  if (!minutes) {
    return `${hours} hr`;
  }
  return `${hours} hr ${minutes} min`;
};

const countWords = (value: string) => String(value || "").trim().split(/\s+/).filter(Boolean).length;

const getEntryStatusLabel = (entry: any) => {
  const normalizedStatus = String(entry?.status || "").trim().toLowerCase();
  if (normalizedStatus === "draft") {
    return "Draft";
  }
  return entry?.isOngoing ? "Ongoing" : "Resolved";
};

const getBehaviorEntryDisplayStaffName = (entry: any) =>
  String(entry?.sourceStaffName || "").trim() || String(entry?.recordedBy || "").trim();
const AI_POLISH_MAX_WORDS = 2000;
const ENTRIES_PER_PAGE = 10;
const QUICK_TIME_OPTIONS = [
  { label: "Now", minutes: 0 },
  { label: "1 hr ago", minutes: 60 },
  { label: "2 hr ago", minutes: 120 },
  { label: "4 hr ago", minutes: 240 },
  { label: "6 hr ago", minutes: 360 },
  { label: "10 hr ago", minutes: 600 },
];
const QUICK_TIME_MATCH_TOLERANCE_MINUTES = 5;
const isEndBeforeStart = (startedAt: string, endedAt: string, isOngoing: boolean) => {
  if (isOngoing || !startedAt || !endedAt) {
    return false;
  }
  const startedAtDate = new Date(toIsoString(startedAt));
  const endedAtDate = new Date(toIsoString(endedAt));
  if (Number.isNaN(startedAtDate.getTime()) || Number.isNaN(endedAtDate.getTime())) {
    return false;
  }
  return endedAtDate.getTime() < startedAtDate.getTime();
};

const isFutureDateTime = (value: string) => {
  if (!value) {
    return false;
  }

  const parsedDate = new Date(toIsoString(value));
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  return parsedDate.getTime() > Date.now();
};

const splitSuggestionText = (values: string[]) => Array.from(new Set(
  values
    .flatMap((value) => String(value || "")
      .split(/\r?\n|[;]+/)
      .map((entry) => entry.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
      .filter(Boolean)),
));

const applySuggestionSelection = (currentValue: string, suggestion: string, checked: boolean) => {
  const lines = String(currentValue || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const nextLines = checked
    ? (lines.includes(suggestion) ? lines : [...lines, suggestion])
    : lines.filter((line) => line !== suggestion);

  return nextLines.join("\n");
};

const applyMultiSuggestionSelection = (
  currentValue: string,
  availableSuggestions: string[],
  selectedSuggestions: string[],
) => {
  const lines = String(currentValue || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const customLines = lines.filter((line) => !availableSuggestions.includes(line));
  return [...selectedSuggestions, ...customLines].join("\n");
};

const trimToMaxWords = (value: string, maxWords: number) => {
  const words = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) {
    return value;
  }

  const limitedWords = words.slice(0, maxWords);
  return limitedWords.join(" ");
};

const toCustomBehaviorStorageKey = (value: string) => {
  const slug = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `custom:${slug || "behavior"}`;
};

const isCustomBehaviorStorageKey = (value: string) => String(value || "").trim().toLowerCase().startsWith("custom:");

const getEntryBehaviorSelections = (entry: any) => {
  const normalizedSelections = Array.isArray(entry?.behaviors)
    ? entry.behaviors
      .map((item: any) => ({
        behaviorKey: isCustomBehaviorStorageKey(String(item?.behaviorKey || "").trim())
          ? CUSTOM_BEHAVIOR_KEY
          : String(item?.behaviorKey || "").trim(),
        behaviorTitle: String(item?.behaviorTitle || "").trim(),
        behaviorCategory: String(item?.behaviorCategory || "").trim(),
      }))
      .filter((item: any) => item.behaviorKey)
    : [];

  if (normalizedSelections.length) {
    return normalizedSelections;
  }

  const behaviorKey = String(entry?.behaviorKey || "").trim();
  const behaviorTitle = String(entry?.behaviorTitle || "").trim();
  const behaviorCategory = String(entry?.behaviorCategory || "").trim();
  if (!behaviorKey && !behaviorTitle) {
    return [];
  }

  return [{
    behaviorKey: isCustomBehaviorStorageKey(behaviorKey) ? CUSTOM_BEHAVIOR_KEY : behaviorKey,
    behaviorTitle: behaviorTitle || behaviorKey || "Behavior",
    behaviorCategory,
  }];
};

const getBehaviorTitlesLabel = (entry: any) => {
  const titles = getEntryBehaviorSelections(entry)
    .map((item) => String(item?.behaviorTitle || item?.behaviorKey || "").trim())
    .filter(Boolean);
  if (!titles.length) {
    return "Behavior";
  }
  return titles.join(", ");
};

const DailyBehaviorLogPanel = ({
  behaviorOptions,
  entries,
  loading = false,
  saving = false,
  emptyMessage = "No daily behavior entries yet.",
  canGenerateReport = false,
  canEditEntry,
  collapsibleComposer = false,
  defaultComposerVisible = true,
  showComposerToggleButton = true,
  composerVisible,
  onComposerVisibleChange,
  openComposerSignal = 0,
  closeComposerSignal = 0,
  showComposerSection = true,
  collapsibleEntries = false,
  defaultEntriesVisible = true,
  entriesVisible,
  onEntriesVisibleChange,
  hideEntriesSection = false,
  showEntriesSection = true,
  showEntriesToggleButton = true,
  showDraftsSection = true,
  serverEntriesPage,
  serverEntriesPageSize = 10,
  serverEntriesHasPreviousPage = false,
  serverEntriesHasNextPage = false,
  onEntriesPreviousPage,
  onEntriesNextPage,
  secondaryActionLabel,
  onCreateEntry,
  onUpdateEntry,
  onDeleteDraft,
  onFormatText,
  onGenerateCaregiverInterventions,
  onGenerateReport,
  onSecondaryAction,
  onDraftSaved,
  onOpenCarePlan,
  openCarePlanLabel = "Go to Manage Care Plan",
  prnMedicationOptions = [],
  loadingPrnMedicationOptions = false,
  allowRecentEntryReuse = true,
}: DailyBehaviorLogPanelProps) => {
  const composerCardRef = useRef<HTMLDivElement | null>(null);
  const [editingEntryId, setEditingEntryId] = useState<string>("");
  const [formState, setFormState] = useState<FormState>(getDefaultFormState());
  const [showExactTimeInputs, setShowExactTimeInputs] = useState<boolean>(false);
  const [showStartQuickTimeOptions, setShowStartQuickTimeOptions] = useState<boolean>(true);
  const [showEndQuickTimeOptions, setShowEndQuickTimeOptions] = useState<boolean>(true);
  const [showComposer, setShowComposer] = useState<boolean>(collapsibleComposer ? defaultComposerVisible : true);
  const [showEntries, setShowEntries] = useState<boolean>(collapsibleEntries ? defaultEntriesVisible : true);
  const [entriesPage, setEntriesPage] = useState<number>(1);
  const [selectedPresentBehaviorSuggestions, setSelectedPresentBehaviorSuggestions] = useState<string[]>([]);
  const [selectedInstructionSuggestions, setSelectedInstructionSuggestions] = useState<string[]>([]);
  const [formattingField, setFormattingField] = useState<"" | "residentPresentBehavior" | "instructionsGiven" | "residentResponse" | "notes">("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showRecentReuseDialog, setShowRecentReuseDialog] = useState<boolean>(false);
  const [selectedRecentReuseEntryId, setSelectedRecentReuseEntryId] = useState<string>("");
  const [showMissingCarePlanBehaviorDialog, setShowMissingCarePlanBehaviorDialog] = useState<boolean>(false);

  const isComposerControlled = typeof composerVisible === "boolean";
  const composerIsVisible = isComposerControlled ? Boolean(composerVisible) : showComposer;
  const isEntriesControlled = typeof entriesVisible === "boolean";
  const entriesAreVisible = isEntriesControlled ? Boolean(entriesVisible) : showEntries;
  const carePlanBehaviorOptions = useMemo(
    () => behaviorOptions.filter((option) => option.behaviorKey !== CUSTOM_BEHAVIOR_KEY),
    [behaviorOptions],
  );
  const hasCarePlanBehaviorOptions = carePlanBehaviorOptions.length > 0;

  useEffect(() => {
    if (!collapsibleComposer || !openComposerSignal) {
      return;
    }
    if (!isComposerControlled) {
      setShowComposer(true);
    }
    onComposerVisibleChange?.(true);
  }, [collapsibleComposer, isComposerControlled, onComposerVisibleChange, openComposerSignal]);

  useEffect(() => {
    if (!collapsibleComposer || !closeComposerSignal) {
      return;
    }
    if (!isComposerControlled) {
      setShowComposer(false);
    }
    onComposerVisibleChange?.(false);
  }, [closeComposerSignal, collapsibleComposer, isComposerControlled, onComposerVisibleChange]);

  useEffect(() => {
    if (!composerIsVisible || hasCarePlanBehaviorOptions) {
      setShowMissingCarePlanBehaviorDialog(false);
      return;
    }
    setShowMissingCarePlanBehaviorDialog(true);
  }, [composerIsVisible, hasCarePlanBehaviorOptions]);

  const selectedBehaviorOptions = useMemo(
    () => formState.behaviorKeys
      .map((behaviorKey) => behaviorOptions.find((option) => option.behaviorKey === behaviorKey) || null)
      .filter(Boolean) as BehaviorOption[],
    [behaviorOptions, formState.behaviorKeys],
  );

  const selectedBehavior = selectedBehaviorOptions[0] || null;

  const behaviorDropdownOptions = useMemo(
    () => behaviorOptions.map((option) => ({
      label: option.sectionCategory
        ? `${option.behaviorTitle} - ${option.sectionCategory}`
        : option.behaviorTitle,
      value: option.behaviorKey,
    })),
    [behaviorOptions],
  );

  const presentBehaviorSuggestions = useMemo(
    () => splitSuggestionText(selectedBehaviorOptions.flatMap((option) => option?.presentBehaviorSuggestions || [])),
    [selectedBehaviorOptions],
  );

  const instructionSuggestions = useMemo(
    () => splitSuggestionText(selectedBehaviorOptions.flatMap((option) => option?.instructionSuggestions || [])),
    [selectedBehaviorOptions],
  );
  const carePlanInstructionSuggestions = useMemo(
    () => splitSuggestionText(selectedBehaviorOptions.flatMap((option) => option?.carePlanInstructions || [])),
    [selectedBehaviorOptions],
  );
  const carePlanInstructionSet = useMemo(() => new Set(carePlanInstructionSuggestions), [carePlanInstructionSuggestions]);
  const genericInstructionSuggestions = useMemo(
    () => instructionSuggestions.filter((suggestion) => !carePlanInstructionSet.has(suggestion)),
    [carePlanInstructionSet, instructionSuggestions],
  );
  const instructionSuggestionOptions = useMemo(
    () => [
      { label: "Care Plan Interventions", items: carePlanInstructionSuggestions.map((suggestion) => ({ label: suggestion, value: suggestion })) },
      { label: "Generic Staff Interventions", items: genericInstructionSuggestions.map((suggestion) => ({ label: suggestion, value: suggestion })) },
    ].filter((group) => group.items.length),
    [carePlanInstructionSuggestions, genericInstructionSuggestions],
  );
  const hasEmarPrnMedicationOptions = prnMedicationOptions.length > 0;
  const resolvedPrnMedicationOptions = useMemo(() => {
    if (
      formState.linkedPrnMedicationId
      && formState.linkedPrnMedicationName
      && !prnMedicationOptions.some((item) => item.value === formState.linkedPrnMedicationId)
    ) {
      return [
        ...prnMedicationOptions,
        {
          label: `${formState.linkedPrnMedicationName} (linked)`,
          value: formState.linkedPrnMedicationId,
          medicationName: formState.linkedPrnMedicationName,
        },
      ];
    }
    return prnMedicationOptions;
  }, [formState.linkedPrnMedicationId, formState.linkedPrnMedicationName, prnMedicationOptions]);

  const resetForm = () => {
    setEditingEntryId("");
    setFormErrors({});
    setShowExactTimeInputs(false);
    setShowStartQuickTimeOptions(true);
    setShowEndQuickTimeOptions(true);
    setSelectedPresentBehaviorSuggestions([]);
    setSelectedInstructionSuggestions([]);
    setFormState({
      ...getDefaultFormState(),
      prnEntryMode: hasEmarPrnMedicationOptions ? "emar" : "manual",
    });
  };

  const scrollComposerToTop = () => {
    window.setTimeout(() => {
      composerCardRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  };

  const startEditingEntry = (entry: any) => {
    const entryBehaviors = getEntryBehaviorSelections(entry);
    const customBehavior = entryBehaviors.find((item) => item.behaviorKey === CUSTOM_BEHAVIOR_KEY);
    if (!isComposerControlled) {
      setShowComposer(true);
    }
    onComposerVisibleChange?.(true);
    scrollComposerToTop();
    setEditingEntryId(String(entry?.behaviorLogEntryId || ""));
    setFormErrors({});
    setShowExactTimeInputs(true);
    setShowStartQuickTimeOptions(false);
    setShowEndQuickTimeOptions(false);
    setSelectedPresentBehaviorSuggestions([]);
    setSelectedInstructionSuggestions([]);
    setFormState({
      behaviorKey: String(entryBehaviors[0]?.behaviorKey || entry?.behaviorKey || behaviorOptions[0]?.behaviorKey || ""),
      behaviorKeys: entryBehaviors.length
        ? entryBehaviors.map((item) => item.behaviorKey)
        : [String(entry?.behaviorKey || behaviorOptions[0]?.behaviorKey || "")].filter(Boolean),
      customBehaviorTitle: String(customBehavior?.behaviorTitle || ""),
      residentPresentBehavior: String(entry?.residentPresentBehavior || ""),
      instructionsGiven: String(entry?.instructionsGiven || ""),
      residentResponse: String(entry?.residentResponse || ""),
      notes: String(entry?.notes || ""),
      startedAt: formatDateTimeLocalInput(entry?.startedAt),
      endedAt: entry?.endedAt ? formatDateTimeLocalInput(entry?.endedAt) : "",
      isOngoing: Boolean(entry?.isOngoing),
      prnUsed: entry?.prnUsed === true,
      prnEntryMode: String(entry?.prnEntryMode || "").trim().toLowerCase() === "emar" ? "emar" : "manual",
      linkedPrnMedicationId: String(entry?.linkedPrnMedicationId || ""),
      linkedPrnMedicationName: String(entry?.linkedPrnMedicationName || ""),
      prnMedicationName: String(entry?.prnMedicationName || ""),
      prnDetails: String(entry?.prnDetails || ""),
    });
  };

  const submitForm = async (targetStatus: "draft" | "final" = "final") => {
    const nextErrors: FormErrors = {};
    const requiresTimeValidation = targetStatus !== "draft";
    if (!formState.behaviorKeys.length) {
      nextErrors.behaviorKey = "Choose a behavior.";
    }
    if (formState.behaviorKeys.includes(CUSTOM_BEHAVIOR_KEY) && !String(formState.customBehaviorTitle || "").trim()) {
      nextErrors.customBehaviorTitle = "Add the custom behavior name.";
    }
    if (requiresTimeValidation && (!String(formState.startedAt || "").trim() || !toIsoString(formState.startedAt))) {
      nextErrors.startedAt = "Start time is required.";
    } else if (requiresTimeValidation && isFutureDateTime(formState.startedAt)) {
      nextErrors.startedAt = "Start time cannot be in the future.";
    }
    if (!String(formState.residentPresentBehavior || "").trim()) {
      nextErrors.residentPresentBehavior = "Present behavior is required.";
    }
    if (!String(formState.instructionsGiven || "").trim()) {
      nextErrors.instructionsGiven = "Instructions given is required.";
    }
    if (requiresTimeValidation) {
      if (formState.isOngoing || !String(formState.endedAt || "").trim() || !toIsoString(formState.endedAt)) {
        nextErrors.endedAt = "Select an end time before saving.";
      } else if (isFutureDateTime(formState.endedAt)) {
        nextErrors.endedAt = "End time cannot be in the future.";
      } else if (isEndBeforeStart(formState.startedAt, formState.endedAt, false)) {
        nextErrors.endedAt = "End time cannot be before start time.";
      }
    }
    if (formState.prnUsed && targetStatus !== "draft") {
      if (formState.prnEntryMode === "emar") {
        if (!String(formState.linkedPrnMedicationId || "").trim()) {
          nextErrors.notes = "Choose a PRN medication from eMAR.";
        }
      } else if (!String(formState.prnMedicationName || "").trim()) {
        nextErrors.notes = "Add the PRN medication name.";
      }
      if (!String(formState.prnDetails || "").trim()) {
        nextErrors.notes = nextErrors.notes || "Add the PRN details.";
      }
    }

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      scrollComposerToTop();
      return;
    }

    if (!selectedBehaviorOptions.length) {
      return;
    }

    const resolvedStatus = targetStatus === "draft"
      ? "draft"
      : (formState.isOngoing ? "open" : "resolved");

    const selectedBehaviorsPayload = selectedBehaviorOptions.map((option) => ({
      behaviorKey: option.behaviorKey === CUSTOM_BEHAVIOR_KEY
        ? toCustomBehaviorStorageKey(formState.customBehaviorTitle)
        : option.behaviorKey,
      behaviorTitle: option.behaviorKey === CUSTOM_BEHAVIOR_KEY
        ? String(formState.customBehaviorTitle || "").trim()
        : option.behaviorTitle,
      behaviorCategory: option.sectionCategory || "",
      residentDetails: String(option.carePlanDetails || "").trim(),
      caregiverInstructions: String(option.caregiverInstructions || "").trim(),
    }));
    const primaryBehaviorPayload = selectedBehaviorsPayload[0];

    const payload = {
      behaviorKey: String(primaryBehaviorPayload?.behaviorKey || ""),
      behaviorTitle: String(primaryBehaviorPayload?.behaviorTitle || ""),
      behaviorCategory: String(primaryBehaviorPayload?.behaviorCategory || ""),
      behaviors: selectedBehaviorsPayload,
      residentPresentBehavior: formState.residentPresentBehavior,
      instructionsGiven: formState.instructionsGiven,
      residentResponse: formState.residentResponse,
      notes: formState.notes,
      startedAt: toIsoString(formState.startedAt),
      endedAt: formState.isOngoing ? "" : toIsoString(formState.endedAt),
      isOngoing: formState.isOngoing,
      status: resolvedStatus,
      prnUsed: formState.prnUsed,
      prnEntryMode: formState.prnUsed ? formState.prnEntryMode : "",
      linkedPrnMedicationId: formState.prnUsed && formState.prnEntryMode === "emar" ? formState.linkedPrnMedicationId : "",
      linkedPrnMedicationName: formState.prnUsed && formState.prnEntryMode === "emar" ? formState.linkedPrnMedicationName : "",
      prnMedicationName: formState.prnUsed
        ? (formState.prnEntryMode === "emar" ? formState.linkedPrnMedicationName : formState.prnMedicationName)
        : "",
      prnDetails: formState.prnUsed ? formState.prnDetails : "",
    };

    if (editingEntryId) {
      await onUpdateEntry(editingEntryId, {
        behaviorLogEntryId: editingEntryId,
        ...payload,
      });
    } else {
      await onCreateEntry(payload);
    }

    resetForm();
    if (targetStatus === "draft") {
      onDraftSaved?.();
    }
  };

  const reuseEntry = (entry: any, mode: "clone" | "behaviors") => {
    if (!allowRecentEntryReuse) {
      return;
    }
    const entryBehaviors = getEntryBehaviorSelections(entry);
    const resolvedBehaviorKeys = entryBehaviors.map((item) => item.behaviorKey).filter(Boolean);
    const customBehavior = entryBehaviors.find((item) => item.behaviorKey === CUSTOM_BEHAVIOR_KEY);
    if (!isComposerControlled) {
      setShowComposer(true);
    }
    onComposerVisibleChange?.(true);
    scrollComposerToTop();
    setEditingEntryId("");
    setFormErrors({});
    setShowExactTimeInputs(false);
    setShowStartQuickTimeOptions(true);
    setShowEndQuickTimeOptions(true);
    setSelectedPresentBehaviorSuggestions([]);
    setSelectedInstructionSuggestions([]);
    setFormState((previousState) => ({
      ...previousState,
      behaviorKey: resolvedBehaviorKeys[0] || behaviorOptions[0]?.behaviorKey || "",
      behaviorKeys: resolvedBehaviorKeys.length ? resolvedBehaviorKeys : [behaviorOptions[0]?.behaviorKey || ""].filter(Boolean),
      customBehaviorTitle: String(customBehavior?.behaviorTitle || ""),
      residentPresentBehavior: mode === "clone" ? String(entry?.residentPresentBehavior || "") : "",
      instructionsGiven: mode === "clone" ? String(entry?.instructionsGiven || "") : "",
      residentResponse: mode === "clone" ? String(entry?.residentResponse || "") : "",
      notes: mode === "clone" ? String(entry?.notes || "") : "",
      startedAt: formatDateTimeLocalInput(),
      endedAt: "",
      isOngoing: true,
      prnUsed: mode === "clone" ? entry?.prnUsed === true : false,
      prnEntryMode: mode === "clone" && String(entry?.prnEntryMode || "").trim().toLowerCase() === "emar" ? "emar" : "manual",
      linkedPrnMedicationId: mode === "clone" ? String(entry?.linkedPrnMedicationId || "") : "",
      linkedPrnMedicationName: mode === "clone" ? String(entry?.linkedPrnMedicationName || "") : "",
      prnMedicationName: mode === "clone" ? String(entry?.prnMedicationName || "") : "",
      prnDetails: mode === "clone" ? String(entry?.prnDetails || "") : "",
    }));
  };

  const resolveEntryNow = async (entry: any) => {
    const behaviorLogEntryId = String(entry?.behaviorLogEntryId || "");
    if (!behaviorLogEntryId) {
      return;
    }

    await onUpdateEntry(behaviorLogEntryId, {
      behaviorLogEntryId,
      isOngoing: false,
      endedAt: new Date().toISOString(),
    });
  };

  const updateStartedAt = (nextValue: string) => {
    setFormErrors((previousState) => ({
      ...previousState,
      startedAt: "",
      endedAt: "",
    }));
    setFormState((previousState) => ({
      ...previousState,
      startedAt: nextValue,
      endedAt: previousState.endedAt && new Date(previousState.endedAt) < new Date(nextValue)
        ? addMinutesToLocalInput(nextValue, 15)
        : previousState.endedAt,
    }));
  };

  const updateEndedAt = (nextValue: string) => {
    setFormErrors((previousState) => ({
      ...previousState,
      endedAt: "",
    }));
    setFormState((previousState) => ({
      ...previousState,
      endedAt: nextValue,
      isOngoing: false,
    }));
  };

  const setBehaviorStillOngoing = () => {
    setFormErrors((previousState) => ({
      ...previousState,
      endedAt: "",
    }));
    setFormState((previousState) => ({
      ...previousState,
      isOngoing: true,
      endedAt: "",
    }));
  };

  const updateStartedAtFromInterval = (minutesAgo: number) => {
    const nextValue = formatDateTimeLocalInput(new Date(Date.now() - minutesAgo * 60000).toISOString());
    setFormErrors((previousState) => ({
      ...previousState,
      startedAt: "",
      endedAt: "",
    }));
    setFormState((previousState) => ({
      ...previousState,
      startedAt: nextValue,
      endedAt: previousState.endedAt && new Date(previousState.endedAt) < new Date(nextValue)
        ? addMinutesToLocalInput(nextValue, 15)
        : previousState.endedAt,
    }));
  };

  const updateEndedAtFromInterval = (minutesAgo: number) => {
    const nextValue = formatDateTimeLocalInput(new Date(Date.now() - minutesAgo * 60000).toISOString());
    updateEndedAt(nextValue);
    return nextValue;
  };

  const resetQuickTimes = () => {
    setShowStartQuickTimeOptions(true);
    setShowEndQuickTimeOptions(true);
    setShowExactTimeInputs(false);
    setFormState((previousState) => ({
      ...previousState,
      startedAt: formatDateTimeLocalInput(),
      endedAt: "",
      isOngoing: true,
    }));
  };

  const selectedStartQuickMinutes = useMemo(() => {
    const startedAtDate = new Date(toIsoString(formState.startedAt));
    if (Number.isNaN(startedAtDate.getTime())) {
      return null;
    }
    const minutesAgo = Math.round((Date.now() - startedAtDate.getTime()) / 60000);
    return QUICK_TIME_OPTIONS
      .map((option) => option.minutes)
      .find((minutes) => Math.abs(minutesAgo - minutes) <= QUICK_TIME_MATCH_TOLERANCE_MINUTES) ?? null;
  }, [formState.startedAt]);

  const selectedEndQuickMinutes = useMemo(() => {
    if (formState.isOngoing || !formState.endedAt) {
      return null;
    }
    const endedAtDate = new Date(toIsoString(formState.endedAt));
    if (Number.isNaN(endedAtDate.getTime())) {
      return null;
    }
    const minutesAgo = Math.round((Date.now() - endedAtDate.getTime()) / 60000);
    return QUICK_TIME_OPTIONS
      .map((option) => option.minutes)
      .find((minutes) => Math.abs(minutesAgo - minutes) <= QUICK_TIME_MATCH_TOLERANCE_MINUTES) ?? null;
  }, [formState.endedAt, formState.isOngoing]);

  const selectedBehaviorDuration = useMemo(
    () => formatBehaviorDuration(formState.startedAt, formState.endedAt, formState.isOngoing),
    [formState.endedAt, formState.isOngoing, formState.startedAt],
  );

  const polishFieldWithAi = async (
    fieldName: "residentPresentBehavior" | "instructionsGiven" | "residentResponse" | "notes",
    domain: string,
  ) => {
    if (!onFormatText) {
      return;
    }

    const sourceText = String(formState[fieldName] || "").trim();
    if (!sourceText) {
      return;
    }

    if (countWords(sourceText) > AI_POLISH_MAX_WORDS) {
      return;
    }

    setFormattingField(fieldName);
    try {
      const response = await onFormatText({
        text: sourceText,
        taskType: "professional-note-cleanup",
        tone: "professional",
        domain,
        outputStyle: "plain_text",
      });
      const formattedText = String(response?.formattedText || "").trim();
      if (!formattedText) {
        return;
      }
      setFormState((previousState) => ({
        ...previousState,
        [fieldName]: trimToMaxWords(formattedText, AI_POLISH_MAX_WORDS),
      }));
    } finally {
      setFormattingField("");
    }
  };

  const confirmDeleteDraft = (entry: any) => {
    const behaviorLogEntryId = String(entry?.behaviorLogEntryId || "").trim();
    if (!behaviorLogEntryId || !onDeleteDraft) {
      return;
    }

    const isDraft = String(entry?.status || "").trim().toLowerCase() === "draft";

    confirmDialog({
      message: renderConfirmMessage(
        isDraft ? "Delete saved draft" : "Delete behavior entry",
        isDraft
          ? "This will permanently delete this unfinished draft."
          : "This behavior entry will be hidden from recent records.",
      ),
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      acceptLabel: isDraft ? "Delete Draft" : "Delete Entry",
      accept: async () => {
        await onDeleteDraft(behaviorLogEntryId);
        if (editingEntryId === behaviorLogEntryId) {
          resetForm();
        }
      },
    });
  };

  useEffect(() => {
    const currentLines = String(formState.residentPresentBehavior || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    setSelectedPresentBehaviorSuggestions(
      presentBehaviorSuggestions.filter((suggestion) => currentLines.includes(suggestion)),
    );
  }, [formState.behaviorKeys, formState.residentPresentBehavior, presentBehaviorSuggestions]);

  useEffect(() => {
    const currentLines = String(formState.instructionsGiven || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    setSelectedInstructionSuggestions(
      instructionSuggestions.filter((suggestion) => currentLines.includes(suggestion)),
    );
  }, [formState.behaviorKeys, formState.instructionsGiven, instructionSuggestions]);

  const generateCaregiverInterventions = async () => {
    if (!onGenerateCaregiverInterventions || !selectedBehaviorOptions.length) {
      return;
    }

    setFormattingField("instructionsGiven");
    try {
      const response = await onGenerateCaregiverInterventions({
        behaviorTitle: selectedBehaviorOptions.map((option) => option.behaviorTitle).join(", "),
        behaviorCategory: selectedBehaviorOptions.map((option) => option.sectionCategory || "").filter(Boolean).join(", "),
        behaviorDetails: selectedBehaviorOptions.map((option) => option.carePlanDetails || "").filter(Boolean).join("\n"),
      });
      const suggestions = splitSuggestionText([String(response?.formattedText || "")]).slice(0, 10);
      if (suggestions.length) {
        setFormState((previousState) => ({
          ...previousState,
          instructionsGiven: trimToMaxWords(suggestions.join("\n"), AI_POLISH_MAX_WORDS),
        }));
        setSelectedInstructionSuggestions([]);
      }
    } finally {
      setFormattingField("");
    }
  };

  const residentPresentBehaviorWordCount = useMemo(() => countWords(formState.residentPresentBehavior), [formState.residentPresentBehavior]);
  const instructionsGivenWordCount = useMemo(() => countWords(formState.instructionsGiven), [formState.instructionsGiven]);
  const residentResponseWordCount = useMemo(() => countWords(formState.residentResponse), [formState.residentResponse]);
  const notesWordCount = useMemo(() => countWords(formState.notes), [formState.notes]);
  const isResidentPresentBehaviorAtLimit = residentPresentBehaviorWordCount >= AI_POLISH_MAX_WORDS;
  const isInstructionsGivenAtLimit = instructionsGivenWordCount >= AI_POLISH_MAX_WORDS;
  const isResidentResponseAtLimit = residentResponseWordCount >= AI_POLISH_MAX_WORDS;
  const isNotesAtLimit = notesWordCount >= AI_POLISH_MAX_WORDS;
  const isServerPaginatedEntries = Boolean(onEntriesPreviousPage || onEntriesNextPage);
  const totalEntriesPages = Math.max(1, Math.ceil(entries.length / ENTRIES_PER_PAGE));
  const normalizedEntriesPage = Math.min(entriesPage, totalEntriesPages);
  const paginatedEntries = useMemo(
    () => isServerPaginatedEntries
      ? entries
      : entries.slice((normalizedEntriesPage - 1) * ENTRIES_PER_PAGE, normalizedEntriesPage * ENTRIES_PER_PAGE),
    [entries, isServerPaginatedEntries, normalizedEntriesPage],
  );
  const draftEntries = useMemo(
    () => entries.filter((entry: any) => String(entry?.status || "").trim().toLowerCase() === "draft"),
    [entries],
  );
  const recentReuseEntries = useMemo(
    () => entries
      .filter((entry: any) => String(entry?.status || "").trim().toLowerCase() !== "draft")
      .slice(0, 10),
    [entries],
  );
  const selectedRecentReuseEntry = useMemo(
    () => recentReuseEntries.find((entry: any) => String(entry?.behaviorLogEntryId || "") === selectedRecentReuseEntryId) || recentReuseEntries[0] || null,
    [recentReuseEntries, selectedRecentReuseEntryId],
  );
  const showRecentReuseSummaryCard = allowRecentEntryReuse && recentReuseEntries.length > 0;
  const showRecentReportsSummaryCard = entries.length > 0;
  const todaysEntries = useMemo(
    () => entries.filter((entry: any) => String(entry?.entryDate || "").trim() === TODAY_DATE_KEY),
    [entries],
  );
  const todaysBehaviorCount = useMemo(
    () => todaysEntries.reduce((total: number, entry: any) => total + Math.max(getEntryBehaviorSelections(entry).length, 1), 0),
    [todaysEntries],
  );
  const todaysCategories = useMemo(
    () => Array.from(new Set(
      todaysEntries.flatMap((entry: any) =>
        getEntryBehaviorSelections(entry)
          .map((item) => String(item?.behaviorCategory || "").trim())
          .filter(Boolean),
      ),
    )),
    [todaysEntries],
  );

  useEffect(() => {
    if (isServerPaginatedEntries) {
      return;
    }
    setEntriesPage((previousState) => Math.min(previousState, totalEntriesPages));
  }, [isServerPaginatedEntries, totalEntriesPages]);

  const updateLimitedTextField = (
    fieldName: "residentPresentBehavior" | "instructionsGiven" | "residentResponse" | "notes",
    nextValue: string,
  ) => {
    setFormErrors((previousState) => ({
      ...previousState,
      [fieldName]: "",
    }));
    setFormState((previousState) => ({
      ...previousState,
      [fieldName]: countWords(nextValue) > AI_POLISH_MAX_WORDS
        ? previousState[fieldName]
        : nextValue,
    }));
  };

  const clearBehaviorError = () => {
    setFormErrors((previousState) => ({
      ...previousState,
      behaviorKey: "",
    }));
  };

  useEffect(() => {
    if (hasEmarPrnMedicationOptions) {
      setFormState((previousState) => (
        previousState.prnUsed && previousState.prnEntryMode === "manual" && !previousState.prnMedicationName && !editingEntryId
          ? { ...previousState, prnEntryMode: "emar" }
          : previousState
      ));
    }
  }, [editingEntryId, hasEmarPrnMedicationOptions]);

  const renderEntriesPaginationControls = () => {
    if (isServerPaginatedEntries) {
      if (!serverEntriesHasPreviousPage && !serverEntriesHasNextPage) {
        return null;
      }

      const currentPage = Math.max(1, serverEntriesPage || 1);
      const startEntryNumber = ((currentPage - 1) * serverEntriesPageSize) + (entries.length > 0 ? 1 : 0);
      const endEntryNumber = ((currentPage - 1) * serverEntriesPageSize) + entries.length;

      return (
        <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
          <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
            {entries.length > 0 ? `Showing ${startEntryNumber}-${endEntryNumber}` : "No entries on this page"}
          </div>
          <div className="flex align-items-center gap-2 flex-wrap">
            <Button
              type="button"
              label="Previous"
              icon="pi pi-chevron-left"
              severity="secondary"
              rounded
              outlined
              size="small"
              onClick={onEntriesPreviousPage}
              disabled={!serverEntriesHasPreviousPage}
            />
            <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", fontWeight: 700 }}>
              Page {currentPage}
            </div>
            <Button
              type="button"
              label="Next"
              iconPos="right"
              icon="pi pi-chevron-right"
              severity="secondary"
              rounded
              outlined
              size="small"
              onClick={onEntriesNextPage}
              disabled={!serverEntriesHasNextPage}
            />
          </div>
        </div>
      );
    }

    if (entries.length <= ENTRIES_PER_PAGE) {
      return null;
    }

    const startEntryNumber = (normalizedEntriesPage - 1) * ENTRIES_PER_PAGE + 1;
    const endEntryNumber = Math.min(normalizedEntriesPage * ENTRIES_PER_PAGE, entries.length);

    return (
      <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
        <div style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
          Showing {startEntryNumber}-{endEntryNumber} of {entries.length}
        </div>
        <div className="flex align-items-center gap-2 flex-wrap">
          <Button
            type="button"
            label="Previous"
            icon="pi pi-chevron-left"
            severity="secondary"
            rounded
            outlined
            size="small"
            onClick={() => setEntriesPage((previousState) => Math.max(1, previousState - 1))}
            disabled={normalizedEntriesPage <= 1}
          />
          <div style={{ ...appMutedTextStyle, fontSize: "0.85rem", fontWeight: 700 }}>
            Page {normalizedEntriesPage} of {totalEntriesPages}
          </div>
          <Button
            type="button"
            label="Next"
            iconPos="right"
            icon="pi pi-chevron-right"
            severity="secondary"
            rounded
            outlined
            size="small"
            onClick={() => setEntriesPage((previousState) => Math.min(totalEntriesPages, previousState + 1))}
            disabled={normalizedEntriesPage >= totalEntriesPages}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-column gap-3">
      {showComposerSection ? (
        <div ref={composerCardRef} style={{ width: "100%", maxWidth: showRecentReuseSummaryCard ? "none" : "56rem", margin: "0 auto" }}>
          <div className={`grid align-items-start ${showRecentReuseSummaryCard ? "" : "justify-content-center"}`.trim()}>
            {showRecentReuseSummaryCard ? (
              <div className="col-12 lg:col-3">
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "16px",
                    padding: "1rem",
                    background: "#f8fafc",
                    display: "grid",
                    gap: "0.9rem",
                    position: "sticky",
                    top: "1rem",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>Quick Reuse From Last 10 Entries</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", marginTop: "0.35rem" }}>
                      Reuse behaviors quickly or clone a recent entry with the full details.
                    </div>
                  </div>
                  <div style={{ display: "grid", gap: "0.6rem" }}>
                    <div style={{ ...appNeutralPanelStyle, padding: "0.8rem 0.9rem" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{recentReuseEntries.length} recent reusable entries</div>
                      <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.25rem" }}>
                        Last entry: {formatTimestamp(recentReuseEntries[0]?.startedAt)}
                      </div>
                    </div>
                    <div style={{ ...appNeutralPanelStyle, padding: "0.8rem 0.9rem" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{todaysBehaviorCount} behaviors logged today</div>
                      <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.25rem" }}>
                        Categories: {todaysCategories.length ? todaysCategories.join(", ") : "No categories yet today"}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    label="Reuse Recent Entries"
                    icon="pi pi-list"
                    severity="warning"
                    rounded
                    onClick={() => {
                      setSelectedRecentReuseEntryId(String(recentReuseEntries[0]?.behaviorLogEntryId || ""));
                      setShowRecentReuseDialog(true);
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div className={`col-12 ${showRecentReuseSummaryCard ? "lg:col-9" : "lg:col-12"}`}>
              <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "none", margin: "0 auto" }}>
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                  {composerIsVisible ? (
                    <div>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>
                        {editingEntryId ? "Update Behavior Entry" : "New Behavior Entry"}
                      </div>
                      <div className="mt-1" style={appMutedTextStyle}>
                        Choose a behavior from the resident's care plan, write what happened, and record the support given.
                      </div>
                      <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                        The behavior details and support ideas below are based on the resident's care plan.
                      </div>
                    </div>
                  ) : <div />}
                  <div className="flex align-items-center gap-2 flex-wrap">
                    {collapsibleComposer && showComposerToggleButton ? (
                      <Button
                        type="button"
                        label={composerIsVisible ? "Hide New Behavior Entry" : "New Behavior Entry"}
                        icon={composerIsVisible ? "pi pi-eye-slash" : "pi pi-plus"}
                        severity="warning"
                        rounded
                        onClick={() => {
                          const nextValue = !composerIsVisible;
                          if (!isComposerControlled) {
                            setShowComposer(nextValue);
                          }
                          onComposerVisibleChange?.(nextValue);
                        }}
                      />
                    ) : null}
                    {secondaryActionLabel && onSecondaryAction ? (
                      <Button
                        type="button"
                        label={secondaryActionLabel}
                        icon="pi pi-share-alt"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={onSecondaryAction}
                      />
                    ) : null}
                    {canGenerateReport && onGenerateReport ? (
                      <Button
                        type="button"
                        label="Generate Report"
                        icon="pi pi-file-export"
                        severity="warning"
                        rounded
                        outlined
                        onClick={onGenerateReport}
                        disabled={entries.length === 0}
                      />
                    ) : null}
                  </div>
                </div>

                {composerIsVisible ? (
                  <>
                    <div className="grid mt-2">
                      <div className="col-12">
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem", textAlign: "center" }}>Time</div>
                        <div
                          style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: "14px",
                            padding: "0.9rem 1rem",
                            background: "#f8fafc",
                            display: "grid",
                            gap: "1rem",
                          }}
                        >
                          <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                              Choose when the behavior started and ended.
                            </div>
                            <div className="flex align-items-center gap-2 flex-wrap">
                              <Button
                                type="button"
                                label="Reset Times"
                                severity="secondary"
                                outlined
                                rounded
                                size="small"
                                onClick={resetQuickTimes}
                              />
                              <Button
                                type="button"
                                label={showExactTimeInputs ? "Hide Exact Times" : "Exact Times"}
                                severity="secondary"
                                rounded
                                outlined
                                size="small"
                                onClick={() => setShowExactTimeInputs((previousState) => !previousState)}
                              />
                            </div>
                          </div>
                          <div style={{ display: "grid", gap: "0.85rem" }}>
                            <div style={{ display: "grid", gap: "0.45rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>Start</div>
                              {!showExactTimeInputs && showStartQuickTimeOptions ? (
                                <div className="flex gap-2 flex-wrap">
                                  {QUICK_TIME_OPTIONS.map((option) => (
                                    <Button
                                      key={`start-${option.label}`}
                                      type="button"
                                      label={option.label}
                                      severity="warning"
                                      outlined={selectedStartQuickMinutes !== option.minutes}
                                      rounded
                                      size="small"
                                      raised={selectedStartQuickMinutes === option.minutes}
                                      onClick={() => {
                                        updateStartedAtFromInterval(option.minutes);
                                        setShowStartQuickTimeOptions(false);
                                      }}
                                    />
                                  ))}
                                </div>
                              ) : null}
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.4 }}>
                                {formatTimestamp(toIsoString(formState.startedAt))}
                              </div>
                              {showExactTimeInputs ? (
                                <div className="grid">
                                  <div className="col-12 md:col-6">
                                    <Calendar
                                      value={getLosAngelesCalendarValue(getLosAngelesDateKey(formState.startedAt))}
                                      onChange={(event) => {
                                        const nextDateKey = getLosAngelesDateKeyFromCalendarValue(event.value as Date | Date[] | null);
                                        if (nextDateKey) {
                                          updateStartedAt(updateLosAngelesDateTimePart(formState.startedAt, nextDateKey));
                                          setShowStartQuickTimeOptions(false);
                                        }
                                      }}
                                      dateFormat="mm/dd/yy"
                                      showIcon
                                      placeholder="Date"
                                      className={`w-full ${formErrors.startedAt ? "p-invalid" : ""}`.trim()}
                                      inputClassName="w-full"
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <Dropdown
                                      value={getLosAngelesTimeKey(formState.startedAt)}
                                      options={LOS_ANGELES_TIME_OPTIONS}
                                      onChange={(event) => {
                                        updateStartedAt(updateLosAngelesDateTimePart(
                                          formState.startedAt,
                                          getLosAngelesDateKey(formState.startedAt),
                                          String(event.value || ""),
                                        ));
                                        setShowStartQuickTimeOptions(false);
                                      }}
                                      placeholder="Time"
                                      className={`w-full ${formErrors.startedAt ? "p-invalid" : ""}`.trim()}
                                    />
                                  </div>
                                </div>
                              ) : null}
                              {formErrors.startedAt ? (
                                <div style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                                  {formErrors.startedAt}
                                </div>
                              ) : null}
                            </div>
                            <div style={{ display: "grid", gap: "0.45rem" }}>
                              <div style={{ fontWeight: 700, color: "#0f172a" }}>End</div>
                              {!showExactTimeInputs && !formState.endedAt ? (
                                <div className="flex gap-2 flex-wrap">
                                  {QUICK_TIME_OPTIONS.map((option) => (
                                    <Button
                                      key={`end-${option.label}`}
                                      type="button"
                                      label={option.label}
                                      severity="warning"
                                      outlined={selectedEndQuickMinutes !== option.minutes}
                                      rounded
                                      size="small"
                                      raised={selectedEndQuickMinutes === option.minutes}
                                      onClick={() => {
                                        const nextValue = updateEndedAtFromInterval(option.minutes);
                                        setShowEndQuickTimeOptions(isEndBeforeStart(formState.startedAt, nextValue, false));
                                      }}
                                    />
                                  ))}
                                </div>
                              ) : null}
                              <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.4, minWidth: 0 }}>
                                {formState.isOngoing ? "No end time yet" : formatTimestamp(toIsoString(formState.endedAt))}
                              </div>
                              {showExactTimeInputs ? (
                                <div className="grid">
                                  <div className="col-12 md:col-6">
                                    <Calendar
                                      value={getLosAngelesCalendarValue(getLosAngelesDateKey(formState.endedAt))}
                                      minDate={getLosAngelesCalendarValue(getLosAngelesDateKey(formState.startedAt)) || undefined}
                                      onChange={(event) => {
                                        const nextDateKey = getLosAngelesDateKeyFromCalendarValue(event.value as Date | Date[] | null);
                                        if (nextDateKey) {
                                          const nextValue = updateLosAngelesDateTimePart(formState.endedAt || formState.startedAt, nextDateKey);
                                          updateEndedAt(nextValue);
                                          setShowEndQuickTimeOptions(isEndBeforeStart(formState.startedAt, nextValue, false));
                                        }
                                      }}
                                      dateFormat="mm/dd/yy"
                                      showIcon
                                      placeholder="Date"
                                      className={`w-full ${formErrors.endedAt ? "p-invalid" : ""}`.trim()}
                                      inputClassName="w-full"
                                    />
                                  </div>
                                  <div className="col-12 md:col-6">
                                    <Dropdown
                                      value={getLosAngelesTimeKey(formState.endedAt)}
                                      options={LOS_ANGELES_TIME_OPTIONS}
                                      onChange={(event) => {
                                        const nextValue = updateLosAngelesDateTimePart(
                                          formState.endedAt || formState.startedAt,
                                          getLosAngelesDateKey(formState.endedAt || formState.startedAt),
                                          String(event.value || ""),
                                        );
                                        updateEndedAt(nextValue);
                                        setShowEndQuickTimeOptions(isEndBeforeStart(formState.startedAt, nextValue, false));
                                      }}
                                      placeholder="Time"
                                      className={`w-full ${formErrors.endedAt ? "p-invalid" : ""}`.trim()}
                                    />
                                  </div>
                                </div>
                              ) : null}
                            </div>
                            {formErrors.endedAt ? (
                              <div style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                                {formErrors.endedAt}
                              </div>
                            ) : null}
                            {selectedBehaviorDuration ? (
                              <div
                                style={{
                                  borderTop: "1px dashed #cbd5e1",
                                  paddingTop: "0.55rem",
                                  color: "#0f172a",
                                  fontSize: "0.9rem",
                                  fontWeight: 700,
                                }}
                              >
                                Total behavior time: {selectedBehaviorDuration}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem", textAlign: "center" }}>Behavior</div>
                        <div className="mb-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem", textAlign: "center" }}>
                          <i>Behavior list comes from the resident's care plan. You can select more than one behavior for the same event.</i>
                        </div>
                        <MultiSelect
                          value={formState.behaviorKeys}
                          options={behaviorDropdownOptions}
                          optionLabel="label"
                          optionValue="value"
                          filter
                          filterBy="label"
                          resetFilterOnHide
                          showSelectAll={false}
                          onChange={(event) => {
                            clearBehaviorError();
                            const nextBehaviorKeys = Array.isArray(event.value) ? event.value.map((item: any) => String(item || "")).filter(Boolean) : [];
                            setFormState((previousState) => ({
                              ...previousState,
                              behaviorKey: nextBehaviorKeys[0] || "",
                              behaviorKeys: nextBehaviorKeys,
                            }));
                            setSelectedInstructionSuggestions([]);
                          }}
                          placeholder="Choose one or more behaviors"
                          display="chip"
                          maxSelectedLabels={4}
                          className={`w-full ${formErrors.behaviorKey ? "p-invalid" : ""}`.trim()}
                        />

                        {formState.behaviorKeys.includes(CUSTOM_BEHAVIOR_KEY) ? (
                          <div className="mt-3" style={{ display: "grid", gap: "0.45rem" }}>
                            <div style={{ fontWeight: 700, color: "#0f172a" }}>Custom Behavior Name</div>
                            <InputText
                              value={formState.customBehaviorTitle}
                              onChange={(event) => {
                                setFormErrors((previousState) => ({
                                  ...previousState,
                                  customBehaviorTitle: "",
                                }));
                                setFormState((previousState) => ({
                                  ...previousState,
                                  customBehaviorTitle: event.target.value,
                                }));
                              }}
                              placeholder="Write the custom behavior name"
                              className={`w-full ${formErrors.customBehaviorTitle ? "p-invalid" : ""}`.trim()}
                            />
                            {formErrors.customBehaviorTitle ? (
                              <div style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                                {formErrors.customBehaviorTitle}
                              </div>
                            ) : null}
                            <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                              Use this when the behavior does not exist in the resident's care plan yet.
                            </div>
                          </div>
                        ) : null}

                      </div>
                      <div className="col-12">
                        <div className="flex align-items-center justify-content-between gap-2" style={{ marginBottom: "0.45rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Resident Present Behavior</div>
                          {onFormatText ? (
                            <Button
                              type="button"
                              label="Clean Up with AI"
                              icon="pi pi-sparkles"
                              severity="secondary"
                              outlined
                              rounded
                              size="small"
                              loading={formattingField === "residentPresentBehavior"}
                              disabled={!formState.residentPresentBehavior.trim() || residentPresentBehaviorWordCount > AI_POLISH_MAX_WORDS}
                              onClick={() => { void polishFieldWithAi("residentPresentBehavior", "resident-behavior-log-present-behavior"); }}
                            />
                          ) : null}
                        </div>
                        {onFormatText ? (
                          <div className="mb-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem", color: isResidentPresentBehaviorAtLimit ? "#b45309" : appMutedTextStyle.color, fontWeight: isResidentPresentBehaviorAtLimit ? 700 : 500 }}>
                            {residentPresentBehaviorWordCount}/{AI_POLISH_MAX_WORDS} words for AI help
                          </div>
                        ) : null}
                        {presentBehaviorSuggestions.length > 0 ? (
                          <div
                            style={{
                              border: "1px solid #e2e8f0",
                              borderRadius: "12px",
                              background: "#f8fafc",
                              padding: "0.75rem",
                              marginBottom: "0.75rem",
                              display: "grid",
                              gap: "0.55rem",
                            }}
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                width: "fit-content",
                                padding: "0.3rem 0.55rem",
                                borderRadius: "999px",
                                background: "#fff7ed",
                                color: "#9a3412",
                                fontSize: "0.78rem",
                                fontWeight: 700,
                              }}
                            >
                              From the care plan: behavior details
                            </div>
                            {presentBehaviorSuggestions.map((suggestion) => (
                              <label key={`present-${suggestion}`} className="flex align-items-start gap-2" style={{ cursor: "pointer" }}>
                                <Checkbox
                                  inputId={`present-${selectedBehavior?.behaviorKey || "behavior"}-${suggestion}`}
                                  checked={selectedPresentBehaviorSuggestions.includes(suggestion)}
                                  onChange={(event) => {
                                    const checked = Boolean(event.checked);
                                    setFormState((previousState) => ({
                                      ...previousState,
                                      residentPresentBehavior: trimToMaxWords(
                                        applySuggestionSelection(previousState.residentPresentBehavior, suggestion, checked),
                                        AI_POLISH_MAX_WORDS,
                                      ),
                                    }));
                                  }}
                                />
                                <span style={{ color: "#334155", lineHeight: 1.5 }}>{suggestion}</span>
                              </label>
                            ))}
                          </div>
                        ) : null}
                        <InputTextarea
                          value={formState.residentPresentBehavior}
                          onChange={(event) => updateLimitedTextField("residentPresentBehavior", event.target.value)}
                          rows={4}
                          autoResize
                          placeholder="Write what you saw."
                          className={`w-full ${formErrors.residentPresentBehavior ? "p-invalid" : ""}`.trim()}
                          style={isResidentPresentBehaviorAtLimit ? { borderColor: "#f59e0b", boxShadow: "0 0 0 1px #f59e0b" } : undefined}
                        />
                        {formErrors.residentPresentBehavior ? (
                          <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                            {formErrors.residentPresentBehavior}
                          </div>
                        ) : null}
                      </div>
                      <div className="col-12">
                        <div className="flex align-items-center justify-content-between gap-2" style={{ marginBottom: "0.45rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Caregiver Interventions Given</div>
                          <div className="flex align-items-center gap-2 flex-wrap justify-content-end">
                            {onGenerateCaregiverInterventions && selectedBehaviorOptions.length > 0 ? (
                              <Button
                                type="button"
                                label="Generate Possible Interventions"
                                icon="pi pi-sparkles"
                                severity="help"
                                outlined
                                rounded
                                size="small"
                                loading={formattingField === "instructionsGiven"}
                                disabled={!selectedBehaviorOptions.length}
                                onClick={() => { void generateCaregiverInterventions(); }}
                              />
                            ) : null}
                            {onFormatText ? (
                              <Button
                                type="button"
                                label="Clean Up with AI"
                                icon="pi pi-sparkles"
                                severity="secondary"
                                outlined
                                rounded
                                size="small"
                                loading={formattingField === "instructionsGiven"}
                                disabled={!formState.instructionsGiven.trim() || instructionsGivenWordCount > AI_POLISH_MAX_WORDS}
                                onClick={() => { void polishFieldWithAi("instructionsGiven", "resident-behavior-log-instructions"); }}
                              />
                            ) : null}
                          </div>
                        </div>
                        {onFormatText ? (
                          <div className="mb-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem", color: isInstructionsGivenAtLimit ? "#b45309" : appMutedTextStyle.color, fontWeight: isInstructionsGivenAtLimit ? 700 : 500 }}>
                            {instructionsGivenWordCount}/{AI_POLISH_MAX_WORDS} words for AI help
                          </div>
                        ) : null}
                        {instructionSuggestions.length > 0 ? (
                          <div
                            style={{
                              border: "1px solid #e2e8f0",
                              borderRadius: "12px",
                              background: "#f8fafc",
                              padding: "0.75rem",
                              marginBottom: "0.75rem",
                              display: "grid",
                              gap: "0.55rem",
                            }}
                          >
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                width: "fit-content",
                                padding: "0.3rem 0.55rem",
                                borderRadius: "999px",
                                background: "#fff7ed",
                                color: "#9a3412",
                                fontSize: "0.78rem",
                                fontWeight: 700,
                              }}
                            >
                              From the care plan: suggested support
                            </div>
                            <MultiSelect
                              value={selectedInstructionSuggestions}
                              options={instructionSuggestionOptions}
                              optionLabel="label"
                              optionValue="value"
                              onChange={(event) => {
                                const selectedValues = Array.isArray(event.value) ? event.value : [];
                                setSelectedInstructionSuggestions(selectedValues);
                                setFormState((previousState) => ({
                                  ...previousState,
                                  instructionsGiven: trimToMaxWords(
                                    applyMultiSuggestionSelection(
                                      previousState.instructionsGiven,
                                      instructionSuggestions,
                                      selectedValues,
                                    ),
                                    AI_POLISH_MAX_WORDS,
                                  ),
                                }));
                              }}
                              display="chip"
                              filter
                              showSelectAll={false}
                              optionGroupLabel="label"
                              optionGroupChildren="items"
                              className="w-full"
                              placeholder="Select one or more caregiver instructions"
                              maxSelectedLabels={3}
                              itemTemplate={(option) => (
                                <div style={{ whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.4 }}>
                                  {option.label}
                                </div>
                              )}
                              panelStyle={{ maxWidth: "min(44rem, 90vw)" }}
                            />
                          </div>
                        ) : null}
                        <InputTextarea
                          value={formState.instructionsGiven}
                          onChange={(event) => updateLimitedTextField("instructionsGiven", event.target.value)}
                          rows={4}
                          autoResize
                          placeholder="Write what support or direction was given."
                          className={`w-full ${formErrors.instructionsGiven ? "p-invalid" : ""}`.trim()}
                          style={isInstructionsGivenAtLimit ? { borderColor: "#f59e0b", boxShadow: "0 0 0 1px #f59e0b" } : undefined}
                        />
                        {formErrors.instructionsGiven ? (
                          <div className="mt-2" style={{ color: "#b45309", fontSize: "0.82rem", fontWeight: 600 }}>
                            {formErrors.instructionsGiven}
                          </div>
                        ) : null}
                      </div>
                      <div className="col-12">
                        <div className="flex align-items-center justify-content-between gap-2" style={{ marginBottom: "0.45rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Resident Response / Outcomes</div>
                          {onFormatText ? (
                            <Button
                              type="button"
                              label="Clean Up with AI"
                              icon="pi pi-sparkles"
                              severity="secondary"
                              outlined
                              rounded
                              size="small"
                              loading={formattingField === "residentResponse"}
                              disabled={!formState.residentResponse.trim() || residentResponseWordCount > AI_POLISH_MAX_WORDS}
                              onClick={() => { void polishFieldWithAi("residentResponse", "resident-behavior-log-response"); }}
                            />
                          ) : null}
                        </div>
                        {onFormatText ? (
                          <div className="mb-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem", color: isResidentResponseAtLimit ? "#b45309" : appMutedTextStyle.color, fontWeight: isResidentResponseAtLimit ? 700 : 500 }}>
                            {residentResponseWordCount}/{AI_POLISH_MAX_WORDS} words for AI help
                          </div>
                        ) : null}
                        <InputTextarea
                          value={formState.residentResponse}
                          onChange={(event) => updateLimitedTextField("residentResponse", event.target.value)}
                          rows={3}
                          autoResize
                          placeholder="Optional: write how the resident responded or whats the outcome"
                          className="w-full"
                          style={isResidentResponseAtLimit ? { borderColor: "#f59e0b", boxShadow: "0 0 0 1px #f59e0b" } : undefined}
                        />
                      </div>
                      <div className="col-12">
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem", textAlign: "center" }}>PRN</div>
                        <div
                          style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: "14px",
                            padding: "0.9rem 1rem",
                            background: "#f8fafc",
                            display: "grid",
                            gap: "0.85rem",
                          }}
                        >
                          <label className="flex align-items-center gap-2" style={{ cursor: "pointer", fontWeight: 600, color: "#0f172a" }}>
                            <Checkbox
                              inputId="behavior-prn-used"
                              checked={formState.prnUsed}
                              onChange={(event) => {
                                const checked = Boolean(event.checked);
                                setFormState((previousState) => ({
                                  ...previousState,
                                  prnUsed: checked,
                                  prnEntryMode: checked
                                    ? (hasEmarPrnMedicationOptions ? previousState.prnEntryMode || "emar" : "manual")
                                    : "manual",
                                  linkedPrnMedicationId: checked ? previousState.linkedPrnMedicationId : "",
                                  linkedPrnMedicationName: checked ? previousState.linkedPrnMedicationName : "",
                                  prnMedicationName: checked ? previousState.prnMedicationName : "",
                                  prnDetails: checked ? previousState.prnDetails : "",
                                }));
                              }}
                            />
                            <span>PRN medication was involved in this behavior entry</span>
                          </label>
                          <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", lineHeight: 1.5 }}>
                            Link to a PRN med in eMAR when available. If eMAR PRN meds are not set up, you can still document it here manually.
                          </div>

                          {formState.prnUsed ? (
                            <div style={{ display: "grid", gap: "0.85rem" }}>
                              {hasEmarPrnMedicationOptions ? (
                                <div className="flex gap-2 flex-wrap">
                                  <Button
                                    type="button"
                                    label="Select from eMAR"
                                    severity="warning"
                                    rounded
                                    outlined={formState.prnEntryMode !== "emar"}
                                    onClick={() => setFormState((previousState) => ({ ...previousState, prnEntryMode: "emar" }))}
                                  />
                                  <Button
                                    type="button"
                                    label="Manual PRN Entry"
                                    severity="info"
                                    rounded
                                    outlined={formState.prnEntryMode !== "manual"}
                                    onClick={() => setFormState((previousState) => ({ ...previousState, prnEntryMode: "manual" }))}
                                  />
                                </div>
                              ) : null}

                              {formState.prnEntryMode === "emar" && hasEmarPrnMedicationOptions ? (
                                <div style={{ display: "grid", gap: "0.75rem" }}>
                                  <div style={{ display: "grid", gap: "0.45rem" }}>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>PRN Medication from eMAR</div>
                                    <Dropdown
                                      value={formState.linkedPrnMedicationId}
                                      options={resolvedPrnMedicationOptions}
                                      optionLabel="label"
                                      optionValue="value"
                                      filter
                                      filterBy="label"
                                      resetFilterOnHide
                                      loading={loadingPrnMedicationOptions}
                                      onChange={(event) => {
                                        const selectedOption = resolvedPrnMedicationOptions.find((item) => item.value === String(event.value || ""));
                                        setFormState((previousState) => ({
                                          ...previousState,
                                          linkedPrnMedicationId: String(event.value || ""),
                                          linkedPrnMedicationName: String(selectedOption?.medicationName || ""),
                                        }));
                                      }}
                                      placeholder={loadingPrnMedicationOptions ? "Loading PRN meds..." : "Choose a PRN medication"}
                                      className="w-full"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div style={{ display: "grid", gap: "0.45rem" }}>
                                  <div style={{ fontWeight: 700, color: "#0f172a" }}>PRN Medication Name</div>
                                  <InputTextarea
                                    value={formState.prnMedicationName}
                                    onChange={(event) => setFormState((previousState) => ({ ...previousState, prnMedicationName: event.target.value }))}
                                    rows={2}
                                    autoResize
                                    placeholder="Write the PRN medication name if eMAR is not set up."
                                    className="w-full"
                                  />
                                </div>
                              )}

                              <div style={{ display: "grid", gap: "0.45rem" }}>
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>PRN Details / Note</div>
                                <InputTextarea
                                  value={formState.prnDetails}
                                  onChange={(event) => setFormState((previousState) => ({ ...previousState, prnDetails: event.target.value }))}
                                  rows={3}
                                  autoResize
                                  placeholder="Describe what PRN was given and any details to keep with this behavior entry."
                                  className="w-full"
                                />
                              </div>

                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="flex align-items-center justify-content-between gap-2" style={{ marginBottom: "0.45rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Notes</div>
                          {onFormatText ? (
                            <Button
                              type="button"
                              label="Clean Up with AI"
                              icon="pi pi-sparkles"
                              severity="secondary"
                              outlined
                              rounded
                              size="small"
                              loading={formattingField === "notes"}
                              disabled={!formState.notes.trim() || notesWordCount > AI_POLISH_MAX_WORDS}
                              onClick={() => { void polishFieldWithAi("notes", "resident-behavior-log-notes"); }}
                            />
                          ) : null}
                        </div>
                        {onFormatText ? (
                          <div className="mb-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem", color: isNotesAtLimit ? "#b45309" : appMutedTextStyle.color, fontWeight: isNotesAtLimit ? 700 : 500 }}>
                            {notesWordCount}/{AI_POLISH_MAX_WORDS} words for AI help
                          </div>
                        ) : null}
                        <InputTextarea
                          value={formState.notes}
                          onChange={(event) => updateLimitedTextField("notes", event.target.value)}
                          rows={3}
                          autoResize
                          placeholder="Optional: add anything else that may help."
                          className="w-full"
                          style={isNotesAtLimit ? { borderColor: "#f59e0b", boxShadow: "0 0 0 1px #f59e0b" } : undefined}
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex justify-content-center gap-2 flex-wrap">
                      {editingEntryId ? (
                        <Button
                          type="button"
                          label="Cancel"
                          severity="secondary"
                          rounded
                          outlined
                          onClick={resetForm}
                        />
                      ) : null}
                      <Button
                        type="button"
                        label={editingEntryId ? "Update Draft" : "Save Draft"}
                        severity="secondary"
                        rounded
                        outlined
                        loading={saving}
                        onClick={() => { void submitForm("draft"); }}
                        disabled={saving}
                      />
                      <Button
                        type="button"
                        label={editingEntryId ? "Update Behavior Entry" : "Save Behavior Entry"}
                        severity="warning"
                        rounded
                        loading={saving}
                        onClick={() => { void submitForm(); }}
                        disabled={saving}
                      />
                    </div>
                  </>
                ) : (
                  <div className="mt-3" style={{ ...appMutedTextStyle, fontSize: "0.92rem" }}>
                    Use <strong>New Behavior Entry</strong> to open the form when you're ready.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showDraftsSection ? (
        <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
          <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
            <div>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>Saved Drafts</div>
              <div style={appMutedTextStyle}>Open a draft and finish it when you're ready.</div>
            </div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "999px",
                padding: "0.3rem 0.7rem",
                background: "#fff7ed",
                color: "#9a3412",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              {draftEntries.length} draft{draftEntries.length === 1 ? "" : "s"}
            </span>
          </div>
          {draftEntries.length ? (
            <div className="mt-3 flex flex-column gap-2">
              {draftEntries.map((entry: any) => (
                <div
                  key={`draft-${entry.behaviorLogEntryId}`}
                  style={{
                    ...appNeutralPanelStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>
                      {getBehaviorTitlesLabel(entry)}
                    </div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                      Started {formatTimestamp(entry?.startedAt)} {getBehaviorEntryDisplayStaffName(entry) ? `| By ${getBehaviorEntryDisplayStaffName(entry)}` : ""}
                    </div>
                  </div>
                  <div className="flex align-items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      label="Resume Draft"
                      icon="pi pi-pencil"
                      severity="warning"
                      rounded
                      outlined
                      onClick={() => startEditingEntry(entry)}
                    />
                    {onDeleteDraft ? (
                      <Button
                        type="button"
                        label="Delete Draft"
                        icon="pi pi-trash"
                        severity="danger"
                        rounded
                        outlined
                        onClick={() => confirmDeleteDraft(entry)}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-3" style={appMutedTextStyle}>No saved drafts yet.</div>
          )}
        </div>
      ) : null}

      {!hideEntriesSection && showEntriesSection ? (
        <div style={{ width: "100%", maxWidth: showRecentReportsSummaryCard ? "none" : "56rem", margin: "0 auto" }}>
          <div className={`grid mt-3 align-items-start ${showRecentReportsSummaryCard ? "" : "justify-content-center"}`.trim()}>
            {showRecentReportsSummaryCard ? (
            <div className="col-12 lg:col-3">
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "16px",
                  padding: "1rem",
                  background: "#f8fafc",
                  display: "grid",
                  gap: "0.85rem",
                  position: "sticky",
                  top: "1rem",
                }}
              >
                <div>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>Recent Reports Summary</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.84rem", marginTop: "0.35rem" }}>
                    Quick view of behavior activity and categories before you review the full entries.
                  </div>
                </div>
                <div style={{ display: "grid", gap: "0.6rem" }}>
                  <div style={{ ...appNeutralPanelStyle, padding: "0.8rem 0.9rem" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{entries.length} recent entries</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.25rem" }}>
                      Latest entry: {entries.length ? formatTimestamp(entries[0]?.startedAt) : "No entries yet"}
                    </div>
                  </div>
                  <div style={{ ...appNeutralPanelStyle, padding: "0.8rem 0.9rem" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{todaysBehaviorCount} behaviors today</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.25rem" }}>
                      Categories: {todaysCategories.length ? todaysCategories.join(", ") : "No categories yet today"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ) : null}
            <div className={`col-12 ${showRecentReportsSummaryCard ? "lg:col-9" : "lg:col-12"}`}>
              <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "none", margin: "0 auto" }}>
                <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                  <div>
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>Recent Behavior Entries</div>
                    <div style={appMutedTextStyle}>Review recent behavior entries and open any record for updates.</div>
                  </div>
                  {collapsibleEntries && showEntriesToggleButton ? (
                    <Button
                      type="button"
                      label={entriesAreVisible ? "Hide Entries" : "Show Entries"}
                      icon={entriesAreVisible ? "pi pi-eye-slash" : "pi pi-list"}
                      severity="secondary"
                      rounded
                      outlined
                      onClick={() => {
                        const nextValue = !entriesAreVisible;
                        if (!isEntriesControlled) {
                          setShowEntries(nextValue);
                        }
                        onEntriesVisibleChange?.(nextValue);
                      }}
                    />
                  ) : null}
                </div>

                {!entriesAreVisible ? (
                  <div className="mt-3" style={{ ...appMutedTextStyle, fontSize: "0.92rem" }}>
                    Use <strong>Show Entries</strong> to see recent entries for this resident.
                  </div>
                ) : loading ? (
                  <div className="flex justify-content-center py-5">
                    <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
                  </div>
                ) : entries.length === 0 ? (
                  <div className="mt-3" style={appMutedTextStyle}>{emptyMessage}</div>
                ) : (
                  <div className="mt-3 flex flex-column gap-3">
                    {renderEntriesPaginationControls()}
                    {paginatedEntries.map((entry: any) => {
                      const editable = canEditEntry ? canEditEntry(entry) : true;
                      const isDraft = String(entry?.status || "").trim().toLowerCase() === "draft";
                      const canDelete = Boolean(onDeleteDraft) && editable;

                      return (
                        <div key={entry.behaviorLogEntryId} style={appNeutralPanelStyle}>
                          <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                            <div>
                              <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                {getBehaviorTitlesLabel(entry)}
                              </div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.85rem" }}>
                                {entry?.behaviorCategory || "Behavior"} | Started {formatTimestamp(entry?.startedAt)}
                              </div>
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                borderRadius: "999px",
                                padding: "0.3rem 0.7rem",
                                background: String(entry?.status || "").trim().toLowerCase() === "draft"
                                  ? "#eef2ff"
                                  : (entry?.isOngoing ? "#fff7ed" : "#ecfdf3"),
                                color: String(entry?.status || "").trim().toLowerCase() === "draft"
                                  ? "#3730a3"
                                  : (entry?.isOngoing ? "#9a3412" : "#166534"),
                                fontWeight: 700,
                                fontSize: "0.8rem",
                              }}
                            >
                              {getEntryStatusLabel(entry)}
                            </span>
                          </div>

                          <div className="grid mt-2">
                            <div className="col-12 lg:col-6">
                              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Present Behavior</div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                {entry?.residentPresentBehavior || "No details documented."}
                              </div>
                            </div>
                            <div className="col-12 lg:col-6">
                              <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Instructions Given</div>
                              <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                {entry?.instructionsGiven || "No instructions documented."}
                              </div>
                            </div>
                            {entry?.residentResponse ? (
                              <div className="col-12 lg:col-6">
                                <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Resident Response</div>
                                <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                  {entry.residentResponse}
                                </div>
                              </div>
                            ) : null}
                            {entry?.notes ? (
                              <div className="col-12 lg:col-6">
                                <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Notes</div>
                                <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                  {entry.notes}
                                </div>
                              </div>
                            ) : null}
                            {entry?.prnUsed ? (
                              <div className="col-12 lg:col-6">
                                <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>PRN</div>
                                <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                                  {entry?.prnMedicationName || entry?.linkedPrnMedicationName || "PRN documented"}
                                  {entry?.prnEntryMode === "emar" && entry?.linkedPrnEntryId ? " | Linked to eMAR" : ""}
                                  {entry?.prnDetails ? `\n${entry.prnDetails}` : ""}
                                </div>
                              </div>
                            ) : null}
                          </div>

                          <div className="mt-3 flex align-items-center justify-content-between gap-2 flex-wrap">
                            <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                              Ended: {entry?.endedAt ? formatTimestamp(entry.endedAt) : "Still ongoing"} | By {getBehaviorEntryDisplayStaffName(entry) || "Unknown"}
                            </div>
                            {editable ? (
                              <div className="flex gap-2">
                                {entry?.isOngoing ? (
                                  <Button
                                    type="button"
                                    label="Resolve Now"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => { void resolveEntryNow(entry); }}
                                  />
                                ) : null}
                                {allowRecentEntryReuse && !isDraft ? (
                                  <Button
                                    type="button"
                                    label="Copy to New Entry"
                                    severity="warning"
                                    rounded
                                    outlined
                                    onClick={() => reuseEntry(entry, "clone")}
                                  />
                                ) : null}
                                <Button
                                  type="button"
                                  label="Edit"
                                  severity="secondary"
                                  rounded
                                  outlined
                                  onClick={() => startEditingEntry(entry)}
                                />
                                {canDelete ? (
                                  <Button
                                    type="button"
                                    label={isDraft ? "Delete Draft" : "Delete"}
                                    severity="danger"
                                    rounded
                                    outlined
                                    onClick={() => confirmDeleteDraft(entry)}
                                  />
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                    {renderEntriesPaginationControls()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <Dialog
        header="No Care Plan Behaviors Yet"
        visible={showMissingCarePlanBehaviorDialog}
        modal
        style={{ width: "min(92vw, 34rem)" }}
        onHide={() => setShowMissingCarePlanBehaviorDialog(false)}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            {onOpenCarePlan ? (
              <Button
                type="button"
                label={openCarePlanLabel}
                severity="warning"
                rounded
                onClick={() => {
                  setShowMissingCarePlanBehaviorDialog(false);
                  onOpenCarePlan();
                }}
              />
            ) : null}
            <Button
              type="button"
              label={onOpenCarePlan ? "Continue Without Care Plan" : "Continue"}
              severity={onOpenCarePlan ? "secondary" : "warning"}
              rounded
              outlined={Boolean(onOpenCarePlan)}
              onClick={() => setShowMissingCarePlanBehaviorDialog(false)}
            />
          </div>
        )}
      >
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <div style={{ color: "#0f172a", fontWeight: 700 }}>
            You need to create the care plan first before you can track behaviors.
          </div>
          <div style={appMutedTextStyle}>
            Add behavior details to the resident&apos;s care plan, then come back here to track them. You can still continue now and use <strong>Custom Behavior</strong> for this entry if needed.
          </div>
        </div>
      </Dialog>
      {allowRecentEntryReuse ? <Dialog
        header="Recent Behavior Reuse"
        visible={showRecentReuseDialog}
        modal
        style={{ width: "min(96vw, 72rem)" }}
        onHide={() => setShowRecentReuseDialog(false)}
        footer={(
          <div
            className="flex align-items-start gap-2 text-left"
            style={{
              background: "#fef3c7",
              border: "2px solid #f59e0b",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(245, 158, 11, 0.22)",
              color: "#92400e",
              padding: "0.85rem 1rem",
              fontSize: "0.84rem",
              fontWeight: 600,
              lineHeight: 1.45,
            }}
          >
            <i className="pi pi-exclamation-triangle mt-1" style={{ color: "#d97706", fontSize: "1rem" }} aria-hidden="true" />
            <span>
              <strong>Audit reminder:</strong> Reused entries are starting points only. Update the date, time, current condition, and any other relevant details so the saved entry accurately reflects the resident&apos;s present situation.
            </span>
          </div>
        )}
      >
        <div className="grid">
          <div className="col-12 lg:col-4">
            <div style={{ display: "grid", gap: "0.65rem" }}>
              {recentReuseEntries.map((entry: any) => {
                const selected = String(entry?.behaviorLogEntryId || "") === String(selectedRecentReuseEntry?.behaviorLogEntryId || "");
                return (
                  <Button
                    key={`reuse-dialog-${entry.behaviorLogEntryId}`}
                    type="button"
                    text
                    onClick={() => setSelectedRecentReuseEntryId(String(entry?.behaviorLogEntryId || ""))}
                    style={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      borderRadius: "12px",
                      border: selected ? "1px solid #f59e0b" : "1px solid #e2e8f0",
                      background: selected ? "#fff7ed" : "#f8fafc",
                      padding: "0.85rem 0.95rem",
                      width: "100%",
                    }}
                    label={`${getBehaviorTitlesLabel(entry)} | ${formatTimestamp(entry?.startedAt)}`}
                  />
                );
              })}
            </div>
          </div>
          <div className="col-12 lg:col-8">
            {selectedRecentReuseEntry ? (
              <div style={{ display: "grid", gap: "0.85rem" }}>
                <div style={{ ...appNeutralPanelStyle, display: "grid", gap: "0.45rem" }}>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>{getBehaviorTitlesLabel(selectedRecentReuseEntry)}</div>
                  <div style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                    Started {formatTimestamp(selectedRecentReuseEntry?.startedAt)}
                    {selectedRecentReuseEntry?.endedAt ? ` | Ended ${formatTimestamp(selectedRecentReuseEntry?.endedAt)}` : " | Still ongoing"}
                    {getBehaviorEntryDisplayStaffName(selectedRecentReuseEntry) ? ` | By ${getBehaviorEntryDisplayStaffName(selectedRecentReuseEntry)}` : ""}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      type="button"
                      label="Reuse this entry"
                      severity="warning"
                      rounded
                      onClick={() => {
                        reuseEntry(selectedRecentReuseEntry, "clone");
                        setShowRecentReuseDialog(false);
                      }}
                    />
                  </div>
                </div>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Present Behavior</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                      {selectedRecentReuseEntry?.residentPresentBehavior || "No details documented."}
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Instructions Given</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                      {selectedRecentReuseEntry?.instructionsGiven || "No instructions documented."}
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Resident Response</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                      {selectedRecentReuseEntry?.residentResponse || "No response documented."}
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div style={{ fontWeight: 700, color: "#334155", fontSize: "0.88rem" }}>Notes</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, whiteSpace: "pre-wrap" }}>
                      {selectedRecentReuseEntry?.notes || "No notes documented."}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={appMutedTextStyle}>No recent entries available.</div>
            )}
          </div>
        </div>
      </Dialog> : null}
    </div>
  );
};

export default DailyBehaviorLogPanel;
